// betup-server-ai admin API (proxied by analytics at /api/dashboard/ai/** → /api/v1/admin/**).
// Contract: betup-server-ai/docs/api.md. All JSON camelCase, timestamps ISO-8601 UTC, days YYYY-MM-DD, money in USD.

export type AiJsonObject = Record<string, unknown>;

export type AiProvider = 'ANTHROPIC' | 'OPENAI' | 'DEEPSEEK';
export type AiDataClass = 'PUBLIC' | 'INTERNAL' | 'PERSONAL';
export type AiStatus = 'ACTIVE' | 'DISABLED';
export type AiModelStatus = 'ACTIVE' | 'DEPRECATED' | 'RETIRED';
export type AiAgentKind = 'DELEGATED' | 'AUTONOMOUS';
export type AiBudgetPeriod = 'DAY' | 'MONTH';
export type AiBudgetAction = 'ALERT' | 'BLOCK';
export type AiBudgetState = 'OK' | 'WARNING' | 'EXCEEDED' | 'BLOCKING';
export type AiBudgetEventType = 'THRESHOLD' | 'LIMIT_REACHED' | 'FIRST_BLOCK' | 'FORECAST';
export type AiRunStatus = 'QUEUED' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
export type AiUsageStatus = 'SUCCESS' | 'ERROR' | 'REFUSAL' | 'TIMEOUT' | 'BLOCKED';
export type AiHealthStatus = 'HEALTHY' | 'WARNING' | 'SICK' | 'DEAD';
export type AiToolKind = 'MCP' | 'SUBAGENT' | 'TASK';
export type AiUsageGroupBy = 'day' | 'caller' | 'task' | 'agent' | 'model';

export const AI_PROVIDERS: AiProvider[] = ['ANTHROPIC', 'OPENAI', 'DEEPSEEK'];
export const AI_DATA_CLASSES: AiDataClass[] = ['PUBLIC', 'INTERNAL', 'PERSONAL'];
export const AI_STATUSES: AiStatus[] = ['ACTIVE', 'DISABLED'];
export const AI_MODEL_STATUSES: AiModelStatus[] = ['ACTIVE', 'DEPRECATED', 'RETIRED'];
export const AI_AGENT_KINDS: AiAgentKind[] = ['DELEGATED', 'AUTONOMOUS'];
export const AI_BUDGET_PERIODS: AiBudgetPeriod[] = ['DAY', 'MONTH'];
export const AI_BUDGET_ACTIONS: AiBudgetAction[] = ['ALERT', 'BLOCK'];
export const AI_RUN_STATUSES: AiRunStatus[] = ['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED'];
export const AI_USAGE_STATUSES: AiUsageStatus[] = ['SUCCESS', 'ERROR', 'REFUSAL', 'TIMEOUT', 'BLOCKED'];
export type AiTaskExecutionStatus = 'SUCCESS' | 'ERROR';
export const AI_TASK_EXECUTION_STATUSES: AiTaskExecutionStatus[] = ['SUCCESS', 'ERROR'];
export const AI_USAGE_GROUP_BYS: AiUsageGroupBy[] = ['day', 'caller', 'task', 'agent', 'model'];

/** Error body of every failed call: `{ message, code, details }`. */
export interface AiErrorDto {
    message: string;
    code: string;
    details: AiJsonObject | null;
}

export interface AiPage<T> {
    items: T[];
    offset: number;
    limit: number;
    totalItems: number;
}

/** Task / agent `params` (all optional). No sampling parameters: models always use their default temperature. */
export interface AiParams {
    thinking?: 'disabled' | 'adaptive';
    effort?: 'low' | 'medium' | 'high' | 'xhigh' | 'max';
    [key: string]: unknown;
}

export interface AiUsageDto {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
}

/** Tokens of one model call in a run step: { input, output, cacheRead, cacheWrite, cacheWrite1h }. */
export interface AiStepUsageDto {
    input: number;
    output: number;
    cacheRead: number;
    cacheWrite: number;
    cacheWrite1h?: number;
}

export interface AiSubjectDto {
    type: string;
    id: string;
}

export interface AiAttachmentDto {
    type: 'image';
    url?: string;
    base64?: string;
    mediaType?: string;
}

// ---- Meta ----

export interface AiMetaDto {
    providers: AiProvider[];
    dataClasses: AiDataClass[];
    statuses: AiStatus[];
    modelStatuses: AiModelStatus[];
    agentKinds: AiAgentKind[];
    budgetPeriods: AiBudgetPeriod[];
    budgetActions: AiBudgetAction[];
    callers: string[];
    credentialPropertyPattern: string;
    mcpCredentialPropertyPattern: string;
    /** What an agent's params.conversation falls back to, and how long conversations are kept. */
    conversationDefaults?: AiConversationDefaultsDto | null;
    /** Tiers named by enabled per-user budgets (suggestions for the tier field). */
    userBudgetTiers?: string[];
}

export interface AiConversationDefaultsDto {
    contextTokens: number;
    replayToolResultChars: number;
    otherConversationsDays: number;
    summaryTask: string;
    retentionDays: number;
    maxConversationRuns: number;
    /** Newest photos sent to the model as images on each run. */
    replayImages?: number;
}

// ---- Models ----

export interface AiModelDto {
    id: number;
    provider: AiProvider;
    modelKey: string;
    inputPerMtok: number;
    outputPerMtok: number;
    cacheWritePerMtok: number | null;
    cacheReadPerMtok: number | null;
    status: AiModelStatus;
    createdAt: string;
    updatedAt: string;
}

export interface AiUpsertModelRequest {
    provider: AiProvider;
    modelKey: string;
    inputPerMtok: number;
    outputPerMtok: number;
    cacheWritePerMtok: number | null;
    cacheReadPerMtok: number | null;
    status: AiModelStatus;
}

// ---- Provider keys ----

export interface AiProviderKeyDto {
    id: number;
    provider: AiProvider;
    name: string;
    credentialProperty: string;
    configured: boolean;
    baseUrl: string | null;
    workspaceRef: string | null;
    maxDataClass: AiDataClass;
    priority: number;
    status: AiStatus;
    lastUsedAt: string | null;
    lastOkAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    updatedBy: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface AiUpsertProviderKeyRequest {
    provider: AiProvider;
    name: string;
    baseUrl: string | null;
    workspaceRef: string | null;
    maxDataClass: AiDataClass;
    priority: number;
    status: AiStatus;
}

// ---- Tasks ----

export interface AiTaskSummaryDto {
    id: number;
    code: string;
    description: string | null;
    ownerService: string | null;
    dataClass: AiDataClass;
    modelKey: string | null;
    status: AiStatus;
    calls7d: number;
    errors7d: number;
    costUsd7d: number;
    updatedAt: string;
}

export interface AiTaskDto {
    id: number;
    code: string;
    description: string | null;
    ownerService: string | null;
    dataClass: AiDataClass;
    systemPrompt: string | null;
    userTemplate: string | null;
    inputSchema: AiJsonObject | null;
    outputSchema: AiJsonObject | null;
    modelId: number | null;
    modelKey: string | null;
    fallbackModelId: number | null;
    fallbackModelKey: string | null;
    providerKeyId: number | null;
    maxOutputTokens: number | null;
    deadlineMs: number | null;
    params: AiParams | null;
    status: AiStatus;
    variables: string[];
    updatedBy: string | null;
    createdAt: string;
    updatedAt: string;
    /** Only opted-in tasks are counted against (and blocked by) per-user budgets. */
    countsTowardUserBudgets?: boolean;
    cacheEnabled?: boolean;
    /** 1–43 200; required while the cache is on. */
    cacheTtlMinutes?: number | null;
    /** null for a task that never cached. */
    cache?: AiTaskCacheStatsDto | null;
    /** Its primary workflow (edited on the workflow): where its calls go when the request names none. */
    workflowCode?: string | null;
    /** Every workflow it belongs to, the primary one first. */
    workflows?: AiWorkflowRefDto[];
}

export interface AiTaskCacheStatsDto {
    entries: number;
    liveEntries: number;
    hits: number;
    lastHitAt: string | null;
}

export interface AiTaskCacheClearDto {
    deleted: number;
}

export interface AiUpsertTaskRequest {
    code: string;
    description: string | null;
    ownerService: string | null;
    dataClass: AiDataClass;
    systemPrompt: string | null;
    userTemplate: string | null;
    inputSchema: AiJsonObject | null;
    outputSchema: AiJsonObject | null;
    modelId: number | null;
    fallbackModelId: number | null;
    providerKeyId: number | null;
    maxOutputTokens: number | null;
    deadlineMs: number | null;
    params: AiParams | null;
    status: AiStatus;
    /** null / absent keeps the current value (false / null for a new task). */
    countsTowardUserBudgets?: boolean | null;
    cacheEnabled?: boolean | null;
    cacheTtlMinutes?: number | null;
}

export interface AiExecuteTaskRequest {
    variables: AiJsonObject;
    attachments?: AiAttachmentDto[];
    subject?: AiSubjectDto | null;
    endUserId?: number | null;
}

export interface AiTaskExecutionDto {
    executionId: number;
    taskCode: string;
    outputText: string | null;
    outputJson: unknown | null;
    model: string;
    providerKey: string;
    fallbackUsed: boolean;
    usage: AiUsageDto;
    costUsd: number;
    latencyMs: number;
}

export interface AiTaskPreviewDto {
    systemPrompt: string | null;
    userPrompt: string;
    variables: string[];
    missingVariables: string[];
}

// ---- Agents ----

export interface AiAgentSummaryDto {
    id: number;
    code: string;
    description: string | null;
    kind: AiAgentKind;
    ownerService: string | null;
    dataClass: AiDataClass;
    modelKey: string | null;
    status: AiStatus;
    runs7d: number;
    failedRuns7d: number;
    costUsd7d: number;
    updatedAt: string;
}

export interface AiAgentDto {
    id: number;
    code: string;
    description: string | null;
    kind: AiAgentKind;
    ownerService: string | null;
    dataClass: AiDataClass;
    systemPrompt: string | null;
    inputSchema: AiJsonObject | null;
    outputSchema: AiJsonObject | null;
    modelId: number | null;
    modelKey: string | null;
    fallbackModelId: number | null;
    fallbackModelKey: string | null;
    providerKeyId: number | null;
    maxOutputTokens: number | null;
    maxTurns: number | null;
    runTimeoutS: number | null;
    /** Runs one conversation may start (conversation mode); null = unlimited. */
    maxConversationRuns?: number | null;
    params: AiParams | null;
    status: AiStatus;
    updatedBy: string | null;
    createdAt: string;
    updatedAt: string;
    /** Only opted-in agents are counted against (and blocked by) per-user budgets. */
    countsTowardUserBudgets?: boolean;
    /** Its primary workflow (edited on the workflow): where its runs go when the request names none. */
    workflowCode?: string | null;
    /** Every workflow it belongs to, the primary one first. */
    workflows?: AiWorkflowRefDto[];
}

export interface AiUpsertAgentRequest {
    code: string;
    description: string | null;
    kind: AiAgentKind;
    ownerService: string | null;
    dataClass: AiDataClass;
    systemPrompt: string | null;
    inputSchema: AiJsonObject | null;
    outputSchema: AiJsonObject | null;
    modelId: number | null;
    fallbackModelId: number | null;
    providerKeyId: number | null;
    maxOutputTokens: number | null;
    maxTurns: number | null;
    runTimeoutS: number | null;
    /** Runs one conversation may start (conversation mode); null = unlimited. */
    maxConversationRuns?: number | null;
    params: AiParams | null;
    status: AiStatus;
    /** null / absent keeps the current value. */
    countsTowardUserBudgets?: boolean | null;
}

/** Bound-arg tokens: `$endUserId`, `$subject.id`, `$subject.type`, `$input.<path>`; anything else is a literal. */
export type AiBoundArgs = Record<string, unknown>;

export interface AiAgentMcpLinkDto {
    serverCode: string;
    serverName: string;
    allowedTools: string[];
    boundArgs: AiBoundArgs | null;
}

export interface AiAgentSubagentLinkDto {
    agentCode: string;
    maxCallsPerRun: number;
}

export interface AiAgentTaskLinkDto {
    taskCode: string;
    maxCallsPerRun: number;
}

export interface AiAgentLinksDto {
    mcpServers: AiAgentMcpLinkDto[];
    subagents: AiAgentSubagentLinkDto[];
    tasks: AiAgentTaskLinkDto[];
}

export interface AiPutMcpLinkRequest {
    allowedTools: string[];
    boundArgs: AiBoundArgs | null;
}

export interface AiMaxCallsRequest {
    maxCallsPerRun: number;
}

export interface AiEffectiveToolDto {
    name: string;
    kind: AiToolKind;
    target: string;
    description: string | null;
    inputSchema: AiJsonObject | null;
    boundArgs: AiBoundArgs | null;
}

export interface AiAgentToolsDto {
    tools: AiEffectiveToolDto[];
    warnings: string[];
}

/** What a call sends as thinking: MODEL_DEFAULT = nothing (the model decides). */
export type AiThinkingMode = 'MODEL_DEFAULT' | 'DISABLED' | 'ADAPTIVE' | 'ENABLED';

/** `POST /effective-params`: `agentCode` of a saved agent (its links decide whether calls carry tools); none for tasks. */
export interface AiEffectiveParamsRequest {
    modelIds: number[];
    params: AiParams | null;
    agentCode?: string | null;
}

export interface AiModelEffectiveParamsDto {
    modelId: number;
    provider: AiProvider;
    modelKey: string;
    thinking: AiThinkingMode;
    effort: string | null;
    /** Why something was changed or dropped. */
    notes: string[];
}

export interface AiEffectiveParamsDto {
    hasTools: boolean;
    models: AiModelEffectiveParamsDto[];
}

export interface AiAgentMessageDto {
    role: 'user' | 'assistant';
    content: string;
}

export interface AiStartAgentRunRequest {
    messages: AiAgentMessageDto[];
    input?: AiJsonObject | null;
    subject?: AiSubjectDto | null;
    endUserId?: number | null;
}

export interface AiAgentRunDto {
    runId: string;
    agentCode: string;
    status: AiRunStatus;
    output: unknown | null;
    outputText: string | null;
    error: string | null;
    turns: number;
    costUsd: number;
    createdAt: string;
    startedAt: string | null;
    finishedAt: string | null;
}

// ---- MCP servers ----

export interface AiMcpServerDto {
    id: number;
    code: string;
    name: string;
    url: string;
    authHeader: string | null;
    credentialRef: string | null;
    credentialProperty: string | null;
    configured: boolean;
    timeoutMs: number | null;
    dataClass: AiDataClass;
    status: AiStatus;
    lastOkAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface AiUpsertMcpServerRequest {
    code: string;
    name: string;
    url: string;
    authHeader: string | null;
    credentialRef: string | null;
    timeoutMs: number | null;
    dataClass: AiDataClass;
    status: AiStatus;
}

export interface AiMcpToolDto {
    name: string;
    title: string | null;
    description: string | null;
    inputSchema: AiJsonObject | null;
    readOnly: boolean | null;
}

/** A resource the server lists (live `resources/list`); context resources of agents read these by uri. */
export interface AiMcpResourceDto {
    uri: string;
    name: string | null;
    title: string | null;
    description: string | null;
    mimeType: string | null;
}

export interface AiMcpCheckDto {
    ok: boolean;
    toolCount: number | null;
    error: string | null;
    checkedAt: string;
}

// ---- Budgets ----

export interface AiBudgetDto {
    id: number;
    name: string;
    taskCode: string | null;
    agentCode: string | null;
    /** Set: only calls attributed to this workflow. */
    workflowCode?: string | null;
    caller: string | null;
    period: AiBudgetPeriod;
    limitUsd: number;
    alertPct: number;
    action: AiBudgetAction;
    enabled: boolean;
    periodStart: string;
    spentUsd: number;
    percent: number;
    forecastUsd: number | null;
    blockedInPeriod: number;
    state: AiBudgetState;
    createdAt: string;
    updatedAt: string;
}

export interface AiUpsertBudgetRequest {
    name: string;
    taskCode: string | null;
    agentCode: string | null;
    workflowCode?: string | null;
    caller: string | null;
    period: AiBudgetPeriod;
    limitUsd: number;
    alertPct: number;
    action: AiBudgetAction;
    enabled: boolean;
}

// ---- Per-user budgets ----

export interface AiUserBudgetDto {
    id: number;
    name: string;
    /** null = all callers. */
    caller: string | null;
    /** agentCode / taskCode both null = every opted-in agent and task. */
    agentCode: string | null;
    taskCode: string | null;
    /** null = the default tier. */
    tier: string | null;
    windowHours: number | null;
    windowLimitUsd: number | null;
    weekLimitUsd: number;
    enabled: boolean;
    createdAt: string;
    updatedAt: string;
}

/** At most one of agentCode / taskCode; windowHours (1–167) and windowLimitUsd together or neither; weekLimitUsd > 0. */
export interface AiUpsertUserBudgetRequest {
    name: string;
    caller: string | null;
    agentCode: string | null;
    taskCode: string | null;
    tier: string | null;
    windowHours: number | null;
    windowLimitUsd: number | null;
    weekLimitUsd: number;
    enabled: boolean;
}

export type AiUserBudgetWindowKind = 'HOURS' | 'WEEK';

export interface AiUserBudgetWindowDto {
    budgetId: number;
    window: AiUserBudgetWindowKind;
    hours: number;
    limitUsd: number;
    usedUsd: number;
    percent: number;
    blocked: boolean;
    resetsAt: string | null;
}

export interface AiEndUserBudgetDto {
    budgetId: number;
    name: string;
    tier: string | null;
    caller: string | null;
    agentCode: string | null;
    taskCode: string | null;
    windows: AiUserBudgetWindowDto[];
}

/** `GET /end-users/{id}/budgets`: per-user budgets that apply, each window's use; `primary` = the window closest to its limit. */
export interface AiEndUserBudgetUsageDto {
    endUserId: number;
    tier: string | null;
    blocked: boolean;
    primary: AiUserBudgetWindowDto | null;
    budgets: AiEndUserBudgetDto[];
}

/** `POST /end-users/{id}/budget-resets`: spend before now stops counting (HOURS = the short windows; WEEK = the week and the short windows). */
export interface AiEndUserBudgetResetRequest {
    window: AiUserBudgetWindowKind;
}

export interface AiBudgetEventDto {
    id: number;
    budgetId: number;
    budgetName: string;
    periodStart: string;
    type: AiBudgetEventType;
    spentUsd: number;
    limitUsd: number;
    forecastUsd: number | null;
    triggerCaller: string | null;
    triggerTaskCode: string | null;
    triggerAgentCode: string | null;
    createdAt: string;
}

// ---- Usage ----

export interface AiUsageRow {
    day: string | null;
    caller: string | null;
    taskCode: string | null;
    agentCode: string | null;
    modelKey: string | null;
    calls: number;
    errors: number;
    blocked: number;
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    costUsd: number;
}

export interface AiUsageSummaryDto {
    from: string;
    to: string;
    groupBy: AiUsageGroupBy[];
    totals: AiUsageRow;
    rows: AiUsageRow[];
}

export interface AiUsageCallDto {
    id: number;
    createdAt: string;
    caller: string;
    taskCode: string | null;
    agentCode: string | null;
    rootAgentCode: string | null;
    runId: string | null;
    endUserId: number | null;
    modelKey: string | null;
    providerKeyName: string | null;
    budgetId: number | null;
    status: AiUsageStatus;
    inputTokens: number;
    outputTokens: number;
    cacheWriteTokens: number;
    cacheReadTokens: number;
    costUsd: number;
    latencyMs: number | null;
    isFallback: boolean;
    subjectType: string | null;
    subjectId: string | null;
    error: string | null;
    /** The call's workflow and run key (the caller's correlation id, e.g. match-brief:12). */
    workflowCode?: string | null;
    workflowRunKey?: string | null;
    webSearches?: number;
    webFetches?: number;
    /** The task execution the call belongs to (variables + output); null for agent turns and calls before hub V12. */
    taskExecutionId?: string | null;
}

export interface AiEndUserUsageDto {
    endUserId: number;
    from: string;
    to: string;
    calls: number;
    costUsd: number;
    rows: AiUsageRow[];
}

// ---- Runs ----

export interface AiRunSummaryDto {
    runId: string;
    agentCode: string;
    parentRunId: string | null;
    rootRunId: string | null;
    depth: number;
    caller: string;
    endUserId: number | null;
    status: AiRunStatus;
    subjectType: string | null;
    subjectId: string | null;
    turns: number;
    costUsd: number;
    error: string | null;
    createdAt: string;
    startedAt: string | null;
    finishedAt: string | null;
}

export interface AiRunStepDto {
    seq: number;
    type: 'MODEL_CALL' | 'TOOL_CALL';
    at: string;
    latencyMs: number | null;
    model: string | null;
    stopReason: string | null;
    /** A model call's tokens as the hub records them in the steps (TokenUsage). */
    usage: AiStepUsageDto | null;
    costUsd: number | null;
    tool: string | null;
    kind: AiToolKind | null;
    target: string | null;
    args: unknown | null;
    result: unknown | null;
    isError: boolean | null;
    childRunId: string | null;
    text: string | null;
}

/** One task execution (hub docs/api.md → Task executions); kept 30 days, inputs may hold player data. */
export interface AiTaskExecutionSummaryDto {
    executionId: string;
    createdAt: string;
    taskCode: string;
    caller: string | null;
    endUserId: number | null;
    /** The agent run that called the task as a tool. */
    runId: string | null;
    subjectType: string | null;
    subjectId: string | null;
    workflowCode: string | null;
    workflowRunKey: string | null;
    status: AiTaskExecutionStatus;
    /** API error code, e.g. OUTPUT_INVALID, REFUSED, PROVIDER_UNAVAILABLE, BUDGET_EXCEEDED. */
    errorCode: string | null;
    error: string | null;
    /** Of the last model call, e.g. MAX_TOKENS. */
    stopReason: string | null;
    cached: boolean;
    modelKey: string | null;
    attachments: number;
    inputTokens: number;
    outputTokens: number;
    costUsd: number;
    latencyMs: number;
    /** First 200 characters of the output text. */
    outputPreview: string | null;
}

export interface AiTaskExecutionDetailDto extends AiTaskExecutionSummaryDto {
    /** null (with `variablesText` set) when the variables were cut at the hub's max-chars. */
    variables: unknown | null;
    variablesText: string | null;
    variablesTruncated: boolean;
    /** Raw text of the last reply, also when it failed (cut off at max tokens, invalid JSON). */
    outputText: string | null;
    outputTruncated: boolean;
    outputJson: unknown | null;
    /** The first reply when the output schema rejected it and a repair attempt followed. */
    rejectedOutputText: string | null;
    calls: AiUsageCallDto[];
}

export interface AiRunDetailDto extends AiRunSummaryDto {
    input: unknown | null;
    output: unknown | null;
    outputText: string | null;
    steps: AiRunStepDto[];
    children: AiRunSummaryDto[];
}

/** `GET /runs/{runId}/export`: one JSON document for analysis (`format` "betup-ai-run-export"); kept as received. */
export interface AiRunExportDto {
    format: string;
    version: number;
    exportedAt: string;
    run: AiRunDetailDto;
    totals: AiJsonObject | null;
    agent: AiJsonObject | null;
    modelCalls: AiJsonObject[];
    toolCalls: AiJsonObject[];
    conversation: AiJsonObject | null;
    childRuns: AiRunDetailDto[];
    [key: string]: unknown;
}

// ---- Privacy ----

export interface AiEndUserDataDeletionDto {
    runsDeleted: number;
    usageRowsAnonymized: number;
    conversationsDeleted: number;
    toolInvocationsDeleted?: number;
    taskExecutionsDeleted?: number;
}

// ---- Conversations ----

export type AiConversationRole = 'USER' | 'OPERATOR' | 'ASSISTANT' | 'TOOL';

export interface AiConversationSummaryDto {
    conversationId: string;
    agentCode: string;
    caller: string | null;
    subjectType: string;
    subjectId: string;
    endUserId: number | null;
    messageCount: number;
    hasSummary: boolean;
    createdAt: string;
    lastMessageAt: string;
}

/**
 * ASSISTANT blocks: tool calls `{ id, name, input }`; TOOL blocks: results `{ toolUseId, content, isError }`; USER blocks:
 * photos `{ type: 'image', url }` the agent was shown.
 */
export interface AiConversationMessageDto {
    messageId: string;
    seq: number;
    externalMessageId: string | null;
    role: AiConversationRole;
    content: string | null;
    blocks: Array<{ id?: string; name?: string; input?: unknown; toolUseId?: string; content?: string; isError?: boolean; type?: string; url?: string }> | null;
    runId: string | null;
    isReply: boolean;
    sentAt: string;
}

export interface AiConversationDetailDto extends Omit<AiConversationSummaryDto, 'messageCount' | 'hasSummary'> {
    summary: string | null;
    summarizedUntilSeq: number | null;
    messages: AiConversationMessageDto[];
}

export interface AiConversationsFilter {
    agentCode?: string;
    endUserId?: string;
    subjectType?: string;
    subjectId?: string;
}

// ---- Health ----

export interface AiHealthBudgetDto {
    budgetId: number;
    name: string;
    scope: { taskCode: string | null; agentCode: string | null; caller: string | null };
    period: AiBudgetPeriod;
    action: AiBudgetAction;
    spentUsd: number;
    limitUsd: number;
    percent: number;
    forecastUsd: number | null;
    blockedInPeriod: number;
    state: AiBudgetState;
    since: string | null;
}

export interface AiHealthProviderKeyDto {
    id: number;
    provider: AiProvider;
    name: string;
    configured: boolean;
    keyStatus: AiStatus;
    status: AiHealthStatus;
    calls15m: number;
    errors15m: number;
    errorRate15m: number;
    lastOkAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
}

export interface AiHealthMcpServerDto {
    code: string;
    name: string;
    /** SICK while failing (e.g. a redeploy); DEAD only after several failures in a row over ~10 minutes. */
    status: AiHealthStatus;
    lastOkAt: string | null;
    lastError: string | null;
    lastErrorAt: string | null;
    /** Failed calls since the last success (0 = healthy) and when that streak started. */
    consecutiveFailures?: number;
    failingSince?: string | null;
}

export interface AiHealthTaskDto {
    code: string;
    status: AiHealthStatus;
    calls1h: number;
    errors1h: number;
    errorRate1h: number;
    fallbackRate1h: number;
    blocked1h: number;
}

export interface AiHealthAgentDto {
    code: string;
    status: AiHealthStatus;
    runs1h: number;
    failedRuns1h: number;
}

export interface AiHealthJobDto {
    name: string;
    lastRunAt: string | null;
    lastSuccessAt: string | null;
    lastError: string | null;
}

export interface AiHealthDto {
    generatedAt: string;
    status: AiHealthStatus;
    budgets: AiHealthBudgetDto[];
    providerKeys: AiHealthProviderKeyDto[];
    mcpServers: AiHealthMcpServerDto[];
    tasks: AiHealthTaskDto[];
    agents: AiHealthAgentDto[];
    jobs: AiHealthJobDto[];
}

/**
 * `GET /api/dashboard/health/ai` (analytics): the contract's AiHealthDto plus availability. When the AI service is
 * unreachable `statusAvailable` is false, `errorMessage` says why and the AiHealthDto fields may be missing/null.
 */
export type DashboardAiHealthDto = {
    [K in keyof AiHealthDto]?: AiHealthDto[K] | null;
} & {
    statusAvailable: boolean;
    errorMessage: string | null;
};

// ---- Import / export (portable specs) ----
// No database ids: models by provider + modelKey, provider keys by provider + name (never the secret),
// MCP servers / tasks / subagents by code, prompts inline.

export const AI_BUNDLE_FORMAT = 'betup-ai-bundle';

export type AiImportMode = 'UPSERT' | 'CREATE_ONLY';
export type AiImportAction = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'SKIP' | 'ERROR';
export type AiImportItemType = 'TASK' | 'AGENT' | 'WORKFLOW';

export const AI_IMPORT_MODES: AiImportMode[] = ['UPSERT', 'CREATE_ONLY'];
export const AI_IMPORT_ACTIONS: AiImportAction[] = ['CREATE', 'UPDATE', 'UNCHANGED', 'SKIP', 'ERROR'];
export const AI_IMPORT_ITEM_TYPES: AiImportItemType[] = ['TASK', 'AGENT'];

export interface AiModelRef {
    provider: AiProvider;
    modelKey: string;
}

export interface AiProviderKeyRef {
    provider: AiProvider;
    name: string;
}

export interface AiTaskSpec {
    code: string;
    description: string | null;
    ownerService: string | null;
    dataClass: AiDataClass;
    systemPrompt: string | null;
    userTemplate: string | null;
    inputSchema: AiJsonObject | null;
    outputSchema: AiJsonObject | null;
    model: AiModelRef | null;
    fallbackModel: AiModelRef | null;
    providerKey: AiProviderKeyRef | null;
    maxOutputTokens: number | null;
    deadlineMs: number | null;
    params: AiParams | null;
    status: AiStatus;
}

export interface AiMcpLinkSpec {
    serverCode: string;
    allowedTools: string[];
    boundArgs: AiBoundArgs | null;
}

export interface AiSubagentLinkSpec {
    agentCode: string;
    maxCallsPerRun: number;
}

export interface AiTaskLinkSpec {
    taskCode: string;
    maxCallsPerRun: number;
}

export interface AiAgentSpec {
    code: string;
    description: string | null;
    kind: AiAgentKind;
    ownerService: string | null;
    dataClass: AiDataClass;
    systemPrompt: string | null;
    inputSchema: AiJsonObject | null;
    outputSchema: AiJsonObject | null;
    model: AiModelRef | null;
    fallbackModel: AiModelRef | null;
    providerKey: AiProviderKeyRef | null;
    maxOutputTokens: number | null;
    maxTurns: number | null;
    runTimeoutS: number | null;
    /** Runs one conversation may start (conversation mode); null = unlimited. */
    maxConversationRuns?: number | null;
    params: AiParams | null;
    status: AiStatus;
    /** On UPSERT these replace the agent's current links. */
    mcpServers: AiMcpLinkSpec[];
    subagents: AiSubagentLinkSpec[];
    tasks: AiTaskLinkSpec[];
}

/** `GET /export` result and `POST /import` body. */
export interface AiBundle {
    format: typeof AI_BUNDLE_FORMAT;
    version: number;
    exportedAt: string | null;
    exportedBy: string | null;
    tasks: AiTaskSpec[];
    agents: AiAgentSpec[];
    /** Workflows and their members by code (importing one sets exactly its members, with their primary flags). */
    workflows?: AiWorkflowSpec[];
}

export interface AiWorkflowSpec {
    code: string;
    name: string;
    description: string | null;
    status: AiStatus;
    members: AiWorkflowMemberDto[];
}

/** `GET /export?tasks=a,b&agents=c&workflows=d&all=false&includeDependencies=true`. */
export interface AiExportRequest {
    tasks?: string[];
    agents?: string[];
    /** A workflow comes with its member tasks and agents. */
    workflows?: string[];
    /** Every task and agent. */
    all?: boolean;
    /** Exported agents bring their linked tasks and subagents (recursively). Server default true. */
    includeDependencies?: boolean;
}

export interface AiImportSummaryDto {
    created: number;
    updated: number;
    unchanged: number;
    skipped: number;
    errors: number;
}

export interface AiImportItemDto {
    type: AiImportItemType;
    code: string;
    action: AiImportAction;
    changedFields: string[] | null;
    messages: string[] | null;
}

/** `POST /import?mode=&dryRun=` → always 200. All-or-nothing: any ERROR item or dryRun → `applied: false`. */
export interface AiImportReportDto {
    dryRun: boolean;
    mode: AiImportMode;
    applied: boolean;
    summary: AiImportSummaryDto;
    items: AiImportItemDto[];
}

// ---- Agent stats (GET /agents/{code}/stats?hours=) ----

export interface AiAgentToolStatDto {
    name: string;
    calls: number;
    errors: number;
}

export interface AiAgentStatsDto {
    agentCode: string;
    modelKey: string | null;
    hours: number;
    from: string;
    runs: number;
    succeeded: number;
    failed: number;
    inProgress: number;
    avgTurns: number;
    avgDurationMs: number | null;
    conversations: number;
    newConversations: number;
    endUsers: number;
    modelCalls: number;
    modelErrors: number;
    blockedCalls: number;
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    costUsd: number;
    toolCalls: number;
    toolErrors: number;
    tools: AiAgentToolStatDto[];
}

// ---- Tool invocations (GET /api/v1/admin/tool-invocations[/stats]) ----------------------------------------------

export type AiToolInvocationStatus = 'SUCCESS' | 'ERROR';
/** TOOL = the tool answered with an error (a refusal, not found…); CALL = it could not be called at all. */
export type AiToolErrorType = 'TOOL' | 'CALL';

export interface AiToolStatDto {
    toolName: string;
    kind: string | null;
    target: string | null;
    calls: number;
    succeeded: number;
    failed: number;
    toolErrors: number;
    callErrors: number;
    failureRate: number;
    avgLatencyMs: number;
    maxLatencyMs: number;
    lastCallAt: string | null;
    lastErrorAt: string | null;
    lastError: string | null;
}

export interface AiToolStatsDto {
    from: string;
    to: string;
    hours: number;
    retentionDays: number;
    bucket: 'HOUR' | 'DAY';
    calls: number;
    succeeded: number;
    failed: number;
    toolErrors: number;
    callErrors: number;
    failureRate: number;
    avgLatencyMs: number;
    tools: AiToolStatDto[];
    series: { start: string; calls: number; failed: number }[];
}

export interface AiToolInvocationDto {
    id: number;
    createdAt: string;
    runId: string;
    agentCode: string;
    rootAgentCode: string;
    caller: string | null;
    endUserId: number | null;
    toolName: string;
    kind: string | null;
    target: string | null;
    status: AiToolInvocationStatus;
    errorType: AiToolErrorType | null;
    errorMessage: string | null;
    latencyMs: number;
}

export interface AiToolInvocationsFilter {
    hours: number;
    agentCode?: string;
    toolName?: string;
    target?: string;
    status?: AiToolInvocationStatus | '';
    errorType?: AiToolErrorType | '';
    runId?: string;
}

// ---- Workflows (groups of agents and tasks, reported and capped together) ----

export type AiWorkflowMemberType = 'AGENT' | 'TASK';

/**
 * A member in a request or a bundle (hub V11, many-to-many): `primary` true makes this workflow the member's primary
 * one (its calls go here when the request names no workflow), false a secondary member, absent keeps it as it is.
 */
export interface AiWorkflowMemberDto {
    type: AiWorkflowMemberType;
    code: string;
    primary?: boolean | null;
}

/** A member as a workflow lists it: whether this is its primary workflow, and its other workflows. */
export interface AiWorkflowMemberInfoDto {
    type: AiWorkflowMemberType;
    code: string;
    primary: boolean;
    alsoIn: string[];
}

/** A workflow an agent or task belongs to. */
export interface AiWorkflowRefDto {
    code: string;
    primary: boolean;
}

export interface AiWorkflowDto {
    id: number;
    code: string;
    name: string;
    description: string | null;
    status: AiStatus;
    members: AiWorkflowMemberInfoDto[];
    costTodayUsd: number;
    cost30dUsd: number;
    runs30d: number;
    updatedBy: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface AiUpsertWorkflowRequest {
    code?: string | null;
    name: string;
    description: string | null;
    status: AiStatus;
    /** The workflow's members become exactly these; a member left out leaves this workflow only. */
    members: AiWorkflowMemberDto[];
}

export interface AiWorkflowDayDto {
    day: string;
    costUsd: number;
    calls: number;
    errors: number;
    blocked: number;
    runs: number;
    webSearches: number;
}

export interface AiWorkflowCostRowDto {
    /** TASK, AGENT or MODEL. */
    type: string;
    code: string;
    costUsd: number;
    calls: number;
    errors: number;
}

/** One workflow instance by its run key (the caller's correlation id): every call, agent runs, cost per member. */
export interface AiWorkflowRunDetailDto {
    code: string;
    name: string;
    runKey: string;
    costUsd: number;
    calls: number;
    errors: number;
    blocked: number;
    webSearches: number;
    webFetches: number;
    inputTokens: number;
    outputTokens: number;
    firstAt: string | null;
    lastAt: string | null;
    members: AiWorkflowCostRowDto[];
    agentRuns: AiRunSummaryDto[];
    /** Every call, oldest first (at most 500). */
    timeline: AiUsageCallDto[];
}

export interface AiWorkflowRunCostDto {
    runKey: string;
    costUsd: number;
    calls: number;
    errors: number;
    webSearches: number;
    firstAt: string;
    lastAt: string;
}

export interface AiWorkflowStatsDto {
    code: string;
    name: string;
    days: number;
    costUsd: number;
    calls: number;
    errors: number;
    blocked: number;
    webSearches: number;
    runs: number;
    avgRunCostUsd: number | null;
    maxRunCostUsd: number | null;
    daily: AiWorkflowDayDto[];
    members: AiWorkflowCostRowDto[];
    models: AiWorkflowCostRowDto[];
    recentRuns: AiWorkflowRunCostDto[];
}

export interface AiWorkflowOverviewDto {
    days: number;
    totalUsd: number;
    unassignedUsd: number;
    workflows: { code: string; name: string; costUsd: number; calls: number; runs: number }[];
    daily: { day: string; totalUsd: number; byWorkflow: Record<string, number> }[];
}
